import React, { useMemo, useState } from 'react';
import { useStore } from '../store-hybrid';
import { BrandLogo } from '../components/BrandLogo';
import { RentalDetailModal } from '../components/RentalDetailModal';
import { SaleDetailModal } from '../components/SaleDetailModal';
import { parseDate, formatDate } from '../utils/dateUtils';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { RentalRecord } from '../types';

type HistoryFilter = 'all' | 'rentals' | 'sales';
type TimeFilter = 'all' | 'today' | 'week' | 'month' | 'year';

export const HistoryPage: React.FC = () => {
  const { cars, brands, models, deleteRentalHistory, deleteSaleHistory } = useStore();
  const [filterType, setFilterType] = useState<HistoryFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRentalRecord, setSelectedRentalRecord] = useState<RentalRecord | null>(null);
  const [showRentalDetailModal, setShowRentalDetailModal] = useState(false);
  const [selectedSaleRecord, setSelectedSaleRecord] = useState<any | null>(null);
  const [showSaleDetailModal, setShowSaleDetailModal] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<{type: 'rental' | 'sale', record: any} | null>(null);

  // Get all rental history from all cars
  const rentalHistory = useMemo(() => {
    const history: (RentalRecord & { carId: string })[] = [];
    cars.forEach(car => {
      if (car.rentalHistory && car.rentalHistory.length > 0) {
        car.rentalHistory.forEach(record => {
          history.push({ ...record, carId: car.id });
        });
      }
    });
    
    
    return history.sort((a, b) => parseDate(b.endDate).getTime() - parseDate(a.endDate).getTime());
  }, [cars]);

  // Get all sales history
  const salesHistory = useMemo(() => {
    return cars
      .filter(car => car.isSold && car.soldDate)
      .map(car => {
        // Handle Firestore Timestamp objects for soldDate
        let saleDate: Date;
        if (typeof car.soldDate === 'object' && 'seconds' in car.soldDate) {
          saleDate = new Date((car.soldDate as any).seconds * 1000);
        } else if (car.soldDate) {
          saleDate = new Date(car.soldDate);
        } else {
          saleDate = new Date(); // Fallback to current date if soldDate is undefined
        }
        
        return {
          id: `sale-${car.id}`,
          carId: car.id,
          saleDate,
          salePrice: car.finalSalePrice || car.salePrice || 0,
          originalPrice: car.salePrice || 0,
          customerName: car.soldToCustomer,
          customerPhone: car.soldToCustomerPhone,
          customerLicense: car.soldToCustomerLicense,
          car: car
        };
      })
      .sort((a, b) => parseDate(b.saleDate).getTime() - parseDate(a.saleDate).getTime());
  }, [cars]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalRentals = rentalHistory.length;
    const totalSales = salesHistory.length;
    const totalRentalRevenue = rentalHistory.reduce((sum, record) => {
      // totalAmount already includes additional charges, no need to add them again
      return sum + record.totalAmount;
    }, 0);
    const totalSalesRevenue = salesHistory.reduce((sum, sale) => sum + sale.salePrice, 0);

    return {
      totalRentals,
      totalSales,
      totalRentalRevenue,
      totalSalesRevenue,
      totalRevenue: totalRentalRevenue + totalSalesRevenue
    };
  }, [rentalHistory, salesHistory]);

  // Filter and combine history
  const combinedHistory = useMemo(() => {
    let combined: any[] = [];

    if (filterType === 'all' || filterType === 'rentals') {
      combined = [...combined, ...rentalHistory.map(r => ({ ...r, type: 'rental' }))];
    }

    if (filterType === 'all' || filterType === 'sales') {
      combined = [...combined, ...salesHistory.map(s => ({ ...s, type: 'sale' }))];
    }

    // Sort by date (most recent first)
    combined.sort((a, b) => {
      const dateA = a.type === 'rental' ? parseDate(a.endDate) : parseDate(a.saleDate);
      const dateB = b.type === 'rental' ? parseDate(b.endDate) : parseDate(b.saleDate);
      return dateB.getTime() - dateA.getTime();
    });

    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (timeFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      combined = combined.filter(item => {
        const itemDate = item.type === 'rental' ? parseDate(item.endDate) : parseDate(item.saleDate);
        return itemDate >= filterDate;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      combined = combined.filter(item => {
        const car = cars.find(c => c.id === item.carId);
        if (!car) return false;
        
        const brand = brands.find(b => b.id === car.brandId);
        const model = models.find(m => m.id === car.modelId);
        
        return (
          brand?.name.toLowerCase().includes(query) ||
          model?.name.toLowerCase().includes(query) ||
          car.plate?.toLowerCase().includes(query) ||
          car.color?.toLowerCase().includes(query)
        );
      });
    }

    return combined;
  }, [rentalHistory, salesHistory, filterType, timeFilter, searchQuery, cars, brands, models]);

  const handleViewRentalDetails = (record: RentalRecord) => {
    setSelectedRentalRecord(record);
    setShowRentalDetailModal(true);
  };

  const handleCloseRentalDetails = () => {
    setSelectedRentalRecord(null);
    setShowRentalDetailModal(false);
  };

  const handleViewSaleDetails = (record: any) => {
    setSelectedSaleRecord(record);
    setShowSaleDetailModal(true);
  };

  const handleCloseSaleDetails = () => {
    setSelectedSaleRecord(null);
    setShowSaleDetailModal(false);
  };

  const handleDeleteRecord = (type: 'rental' | 'sale', record: any) => {
    setRecordToDelete({ type, record });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!recordToDelete) return;

    const { type, record } = recordToDelete;
    
    if (type === 'rental') {
      deleteRentalHistory(record.carId, record.id);
    } else if (type === 'sale') {
      deleteSaleHistory(record.carId);
    }

    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">📊 Transaction History</h1>
        <p className="text-gray-600 dark:text-gray-400">Complete history of all rentals and sales</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Rentals</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalRentals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Sales</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSales}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Rental Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{formatSimpleCurrency(stats.totalRentalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Sales Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{formatSimpleCurrency(stats.totalSalesRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{formatSimpleCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6 sm:mb-8 overflow-hidden">
        {/* Filter Header - Always Visible */}
        <div 
          className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 dark:bg-gray-900 transition-colors border-b border-gray-100 dark:border-gray-700"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
              {!filtersExpanded && (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                    {filterType === 'all' ? 'All' : filterType === 'rentals' ? 'Rentals' : 'Sales'}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                    {timeFilter === 'all' ? 'All Time' : timeFilter}
                  </span>
                  {searchQuery && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                      "{searchQuery}"
                    </span>
                  )}
                </div>
              )}
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Filter Content - Expandable */}
        <div className={`transition-all duration-300 ease-in-out ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Type
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as HistoryFilter)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Transactions</option>
                  <option value="rentals">Rentals Only</option>
                  <option value="sales">Sales Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Period
                </label>
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="year">Last Year</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by brand, model, plate..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {(filterType !== 'all' || timeFilter !== 'all' || searchQuery) && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => {
                    setFilterType('all');
                    setTimeFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Transaction History ({combinedHistory.length} records)
          </h3>
        </div>

        {combinedHistory.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No transactions found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {combinedHistory.map((record, index) => (
              <HistoryRecord 
                key={`${record.type}-${record.id}-${index}`} 
                record={record} 
                onViewDetails={record.type === 'rental' ? () => handleViewRentalDetails(record) : () => handleViewSaleDetails(record)}
                onDelete={() => handleDeleteRecord(record.type, record)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rental Detail Modal */}
      {selectedRentalRecord && (
        <RentalDetailModal
          isOpen={showRentalDetailModal}
          onClose={handleCloseRentalDetails}
          record={selectedRentalRecord}
          carInfo={(() => {
            const car = cars.find(c => c.id === (selectedRentalRecord as any).carId);
            if (!car) return null;
            const brand = brands.find(b => b.id === car.brandId);
            const model = models.find(m => m.id === car.modelId);
            return {
              brandId: car.brandId,
              brand: brand?.name || '',
              model: model?.name || '',
              plate: car.plate,
              year: car.year,
              color: car.color
            };
          })()}
        />
      )}

      {/* Sale Detail Modal */}
      {selectedSaleRecord && (
        <SaleDetailModal
          isOpen={showSaleDetailModal}
          onClose={handleCloseSaleDetails}
          record={selectedSaleRecord}
          carInfo={(() => {
            const car = cars.find(c => c.id === selectedSaleRecord.carId);
            if (!car) return null;
            const brand = brands.find(b => b.id === car.brandId);
            const model = models.find(m => m.id === car.modelId);
            return {
              brandId: car.brandId,
              brand: brand?.name || '',
              model: model?.name || '',
              plate: car.plate,
              year: car.year,
              color: car.color
            };
          })()}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && recordToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete History Record</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this {recordToDelete.type === 'rental' ? 'rental' : 'sale'} record? 
              {recordToDelete.type === 'sale' && ' This will make the car available for sale again.'}
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelDelete}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// History Record Component
const HistoryRecord: React.FC<{ record: any; onViewDetails?: () => void; onDelete?: () => void }> = ({ record, onViewDetails, onDelete }) => {
  const { cars, brands, models } = useStore();
  const car = cars.find(c => c.id === record.carId);
  const brand = brands.find(b => b.id === car?.brandId);
  const model = models.find(m => m.id === car?.modelId);

  if (!car) return null;

  const isRental = record.type === 'rental';
  const date = isRental ? parseDate(record.endDate) : parseDate(record.saleDate);
  const amount = isRental ? record.totalAmount : record.salePrice;

  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
          <div className="flex-shrink-0">
            <BrandLogo brandId={car.brandId} size="md" />
          </div>
          
          <div className="min-w-0 flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-1 sm:space-y-0">
              <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                {brand?.name} {model?.name}
              </h4>
              <span className={`px-2 py-1 text-xs font-medium rounded-full self-start ${
                isRental 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-purple-100 text-purple-800'
              }`}>
                {isRental ? '🚗 Rental' : '💰 Sale'}
              </span>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
              <span>{car.plate}</span>
              <span>{car.year}</span>
              <span className="capitalize">{car.color}</span>
              {isRental && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span>{record.totalDays} days</span>
                  {record.startMileage && record.endMileage && (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span className="text-xs sm:text-sm">{(record.endMileage - record.startMileage).toLocaleString()} km driven</span>
                    </>
                  )}
                </>
              )}
              {!isRental && record.customerName && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-purple-600 font-medium">Sold to: {record.customerName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start space-x-4 sm:space-x-0 sm:space-y-2 flex-shrink-0">
          <div className="text-left sm:text-right">
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {formatSimpleCurrency(amount)}
            </div>
            <div className="text-sm text-gray-500">
              {formatDate(date)}
            </div>
            {isRental && record.additionalCharges && record.additionalCharges.length > 0 && (
              <div className="text-xs text-orange-600 mt-1">
                +{formatSimpleCurrency(record.additionalCharges.reduce((sum: number, charge: any) => sum + charge.amount, 0))} additional charges
              </div>
            )}
            {!isRental && record.originalPrice && record.originalPrice !== amount && (
              <div className="text-xs text-gray-500 mt-1">
                Listed: {formatSimpleCurrency(record.originalPrice)}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-2">
            {onViewDetails && (
              <button
                onClick={onViewDetails}
                className={`px-3 py-1 text-xs text-white rounded-lg transition-colors whitespace-nowrap ${
                  isRental 
                    ? 'bg-blue-500 hover:bg-blue-600' 
                    : 'bg-purple-500 hover:bg-purple-600'
                }`}
              >
                View Details
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                title="Delete record"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Additional Charges Details */}
      {isRental && record.additionalCharges && record.additionalCharges.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Additional Charges:</h5>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {record.additionalCharges.map((charge: any, index: number) => (
              <div key={index} className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">{charge.description}</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatSimpleCurrency(charge.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
